import { z } from 'zod';
import type { Chat } from './chat.js';
import { ChatActionType } from '../enums/index.js';
import { Quant } from './chat.chart.js';

// Shared form data structure for log interaction actions
export const FormDataItemModel = z.object({
  fieldLabel: z.string(),
  value: z.any(),
});
export type FormDataItem = z.infer<typeof FormDataItemModel>;

export const FormDataArrayModel = z.array(FormDataItemModel).optional();
export type FormDataArray = z.infer<typeof FormDataArrayModel>;

export const ReaskWithHcpModel = z.object({
  type: z.literal(ChatActionType.ReaskWithHcp),
  payload: z.object({
    hcpId: z.number(),
    queryId: z.number(),
  }),
});
export type ReaskWithHcpChatAction = z.infer<typeof ReaskWithHcpModel>;

export const LocationItemModel = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('browser'),
    latitude: z.number(),
    longitude: z.number(),
  }),
  z.object({
    type: z.literal('search'),
    longitude: z.number(),
    latitude: z.number(),
    name: z.string(),
    id: z.string(),
  }),
  z.object({
    type: z.literal('hcp'),
    longitude: z.number(),
    latitude: z.number(),
    name: z.string(),
    id: z.number(),
  }),
]);
// Discriminated union for SelectLocation chat action
export type SelectLocationItem = z.infer<typeof LocationItemModel>;

export const SelectLocationModel = z.object({
  type: z.literal(ChatActionType.SelectLocation),
  payload: z.object({
    location: LocationItemModel,
    queryId: z.number(),
  }),
});
export type SelectionLocationChatAction = DeepRequired<
  z.infer<typeof SelectLocationModel>
>;

export const BuildRouteModel = z.object({
  type: z.literal(ChatActionType.BuildRoute),
  payload: z.object({
    origin: z.object({
      latitude: z.number(),
      longitude: z.number(),
      address: z.string(),
      kolId: z.number().nullable(),
    }),
    kols: z.array(
      z.object({
        kolId: z.number(),
        location: z.optional(
          z.object({
            latitude: z.number(),
            longitude: z.number(),
            address: z.string(),
          }),
        ),
      }),
    ),
  }),
});
export type BuildRouteChatAction = z.infer<typeof BuildRouteModel>;

export const SubmitFormWorkflowModel = z.object({
  type: z.literal(ChatActionType.SubmitFormWorkflow),
  payload: z.object({
    queryId: z.number(),
    workflowType: z.string(), // e.g., 'log-interaction', 'schedule-interaction', 'patient-survey', etc.
    formParentId: z.number().optional(),
    formVersion: z.string().optional(),
    formState: z.enum([
      'initial',
      'manual-entry',
      'speech-entry',
      'draft',
      'submitted',
      'cancelled',
      'probing',
    ]).optional(),
    formData: FormDataArrayModel,
    aiState: z.any().optional(),
  }),
});
export type SubmitFormWorkflowChatAction = z.infer<
  typeof SubmitFormWorkflowModel
>;

export const ChartQuantAnalysesChatActionModel = z.object({
  type: z.literal(ChatActionType.ChartQuantAnalyses),
  quantId: z.number(),
  quantItemId: z.number(),
  query: z.string(),
  projectId: z.number(),
});
export type ChartQuantAnalysesChatAction = z.infer<
  typeof ChartQuantAnalysesChatActionModel
>;

export const QueryFilesChatActionModel = z.object({
  type: z.literal(ChatActionType.QueryFiles),
  query: z.string(),
});
export type QueryFilesChatAction = z.infer<typeof QueryFilesChatActionModel>;

export const FindQuotesChatActionModel = z.object({
  type: z.literal(ChatActionType.FindQuotes),
  subject: z.string(),
});
export type FindQuotesChatAction = z.infer<typeof FindQuotesChatActionModel>;

// Zod schema for Chat.Attachment.AskSurveyQuestionSuggestedAction
export const QuerySurveyQuestionChatActionModel = z.object({
  type: z.literal(ChatActionType.QuerySurveyQuestion),
  project: z.object({
    id: z.number(),
    name: z.string(),
  }),
  question: z.object({
    id: z.number(),
    base: z.object({ id: z.number() }),
    ordinal: z.number(),
    value: z.string(),
  }),
  survey: z.object({
    id: z.number(),
    name: z.string(),
  }),
});
export type QuerySurveyQuestionChatAction = z.infer<typeof QuerySurveyQuestionChatActionModel>;

export type SmartRoutingChatAction = {
  type: ChatActionType.SmartRouting;
  payload: {
    smartRoutingState: Chat.QueryState.SmartRouting;
  };
};

export const SummarizeFolderChatActionModel = z.object({
  type: z.literal(ChatActionType.SummarizeFolder),
  payload: z.object({
    folderId: z.number(),
  }),
});
export type SummarizeFolderChatAction = z.infer<typeof SummarizeFolderChatActionModel>;

export const LoadDraftModel = z.object({
  type: z.literal(ChatActionType.LoadDraft),
  payload: z.object({
    queryId: z.number(),
    draftId: z.string(),
  }),
});
export type LoadDraftChatAction = z.infer<typeof LoadDraftModel>;

export type AnswerQuantAnalysesAction = {
  quantId: number;
  quantItemId: number;
  query: string;
  projectId: number;
  type: ChatActionType.AnswerQuantAnalyses;
};

export type ChartQuantAnalysesAction = {
  quantId: number;
  quantItemId: number;
  query: string;
  projectId: number;
  type: ChatActionType.ChartQuantAnalyses;
};

export type FindQuantAnalysesAction = {
  quants: Quant[];
  query: string;
  projectId: number;
  type: ChatActionType.FindQuantAnalyses;
};

export type StartSegmentAnalysisAction = {
  type: ChatActionType.StartSegmentAnalysis;
};

export type StartSegmentationBuilderAction = {
  type: ChatActionType.StartSegmentationBuilder;
};

export type ChatWithSegmentAction = {
  type: ChatActionType.ChatWithSegment;
  fromSidebar?: boolean;
};

export type SelectSegmentForChatAction = {
  type: ChatActionType.SelectSegmentForChat;
  segmentIdentifier: string;
  segmentId: number;
  /** Passed from SegmentChatNeeded data — no extra server fetch needed */
  groupTagId: number;
  groupTagName: string;
  iconUrl?: string | null;
};

export type ChatAction<T extends ChatActionType = ChatActionType> = {
  [ChatActionType.ReaskWithHcp]: ReaskWithHcpChatAction;
  [ChatActionType.SelectLocation]: SelectionLocationChatAction;
  [ChatActionType.BuildRoute]: BuildRouteChatAction;
  [ChatActionType.SmartRouting]: SmartRoutingChatAction;
  [ChatActionType.ChartQuantAnalyses]: ChartQuantAnalysesChatAction;
  [ChatActionType.QueryFiles]: QueryFilesChatAction;
  [ChatActionType.FindQuotes]: FindQuotesChatAction;
  [ChatActionType.SubmitFormWorkflow]: SubmitFormWorkflowChatAction;
  [ChatActionType.QuerySurveyQuestion]: QuerySurveyQuestionChatAction;
  [ChatActionType.SummarizeFolder]: SummarizeFolderChatAction;
  [ChatActionType.LoadDraft]: LoadDraftChatAction;
  [ChatActionType.AnswerQuantAnalyses]: AnswerQuantAnalysesAction;
  [ChatActionType.FindQuantAnalyses]: FindQuantAnalysesAction;
  [ChatActionType.StartSegmentAnalysis]: StartSegmentAnalysisAction;
  [ChatActionType.ChatWithSegment]: ChatWithSegmentAction;
  [ChatActionType.SelectSegmentForChat]: SelectSegmentForChatAction;
  [ChatActionType.StartSegmentationBuilder]: StartSegmentationBuilderAction;
}[T];

type DeepRequired<T> = {
  [K in keyof T]: Required<DeepRequired<T[K]>>;
};
